import React, { useEffect, useState } from 'react';
import { Truck, Clock } from 'lucide-react';
const CUTOFF_HOUR = 12; // 12 PM (noon) Pacific
const WINDOW_START_HOUR = 6; // Mon–Fri dispatch opens 6 AM
const SAT_WINDOW_START_HOUR = 7; // Saturday dispatch opens 7 AM
/**
 * Returns Pacific time as { hour, minute, day }.
 * day: 0 = Sun, 1 = Mon, ..., 6 = Sat
 */
function getPacificNow() {
  const now = new Date();
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false
  });
  const parts = fmt.formatToParts(now);
  const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
  const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
  const weekday = parts.find(p => p.type === 'weekday')?.value || 'Mon';
  const dayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6
  };
  return {
    hour,
    minute,
    day: dayMap[weekday] ?? 1
  };
}
type Status = {
  sameDay: boolean;
  label: string;
  /**
   * 0–1 ratio of same-day window remaining (1 = just opened, 0 = at cutoff).
   * Only meaningful when sameDay is true.
   */
  progress: number;
};
function computeStatus(): Status {
  const {
    hour,
    minute,
    day
  } = getPacificNow();
  // Sunday: dispatch closed
  if (day === 0) {
    return {
      sameDay: false,
      label: 'Order now — first delivery Monday',
      progress: 0
    };
  }
  // Saturday: deliveries end at 1 PM. Before noon, same-day still possible (call to verify).
  if (day === 6) {
    if (hour < CUTOFF_HOUR) {
      return {
        sameDay: false,
        label: 'Saturday delivery ends 1 PM — call to verify same-day',
        progress: 0
      };
    }
    return {
      sameDay: false,
      label: 'Saturday cutoff passed — call to check availability',
      progress: 0
    };
  }
  // Mon–Fri before 12 PM cutoff
  const minutesUntilCutoff = (CUTOFF_HOUR - hour) * 60 - minute;
  if (minutesUntilCutoff > 0) {
    const hrs = Math.floor(minutesUntilCutoff / 60);
    const mins = minutesUntilCutoff % 60;
    let countdown = '';
    if (hrs > 0 && mins > 0) countdown = `${hrs}h ${mins}m`;else if (hrs > 0) countdown = `${hrs}h`;else countdown = `${mins}m`;
    // Window length from dispatch open → cutoff (Mon–Fri: 6 hours)
    const totalWindowMinutes = (CUTOFF_HOUR - WINDOW_START_HOUR) * 60;
    const progress = Math.max(0, Math.min(1, minutesUntilCutoff / totalWindowMinutes));
    return {
      sameDay: true,
      label: `Get it today — order in ${countdown}`,
      progress
    };
  }
  // Mon–Fri after 12 PM cutoff — same-day may still be possible, but only by phone
  return {
    sameDay: false,
    label: 'After 12 PM — call to check same-day availability',
    progress: 0
  };
}
interface Props {
  /** Compact = small inline pill; full = wider banner with countdown progress bar */
  variant?: 'compact' | 'full';
  className?: string;
}
export function SameDayBadge({
  variant = 'compact',
  className = ''
}: Props) {
  const [status, setStatus] = useState<Status>(() => computeStatus());
  useEffect(() => {
    // Update every 30s so the countdown + progress bar stay fresh
    const t = setInterval(() => setStatus(computeStatus()), 30000);
    return () => clearInterval(t);
  }, []);
  const Icon = status.sameDay ? Truck : Clock;
  const colorClasses = status.sameDay ? 'bg-[#1DB954]/10 text-[#1DB954] border-[#1DB954]/30' : 'bg-stone-900/5 dark:bg-white/5 text-stone-700 dark:text-stone-300 border-stone-300/60 dark:border-white/10';
  if (variant === 'full') {
    // After the 12 PM cutoff (and on Sat/Sun) the same-day pill disappears
    // entirely. It reappears automatically at midnight on the next work day,
    // because computeStatus returns sameDay = true for Mon–Fri 12:00 AM–11:59 AM.
    if (!status.sameDay) return null;
    return <div className={`text-[#1DB954] py-2 ${className}`} data-id="element-1127">
        <div className="flex items-center gap-2" data-id="element-1128">
          <Icon className="w-4 h-4 shrink-0 animate-pulse" data-id="element-1129" />
          <span className="text-[12px] font-[500]" data-id="element-1130">{status.label}</span>
        </div>

        {/* Countdown progress bar — full at start of order window, shrinks
                    linearly toward 0 as the 12 PM cutoff approaches. */}
        <div className="mt-2 h-1 w-full bg-[#1DB954]/15 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(status.progress * 100)} aria-label="Time remaining before same-day cutoff" data-id="element-1131">
          <div className="h-full bg-[#1DB954] transition-[width] duration-700 ease-out" style={{
          width: `${status.progress * 100}%`
        }} data-id="element-1132" />
        </div>
      </div>;
  }
  return <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-bold ${colorClasses} ${className}`} data-id="element-1133">
      <Icon className={`w-3 h-3 shrink-0 ${status.sameDay ? 'animate-pulse' : ''}`} data-id="element-1134" />
      <span data-id="element-1135">{status.label}</span>
    </div>;
}